import classnames from 'classnames';
import {find} from 'lodash';
import React, {useEffect, useRef} from 'react';
import Text from 'shared/components/text';
import {
  EDUCATOR_SORT_OPTIONS,
  HS_SORT_OPTIONS,
  pluralize,
  PUBLIC_SORT_OPTIONS,
  TRANSFER_SORT_OPTIONS,
} from 'shared/utils';
import SortIcon from 'src/components/CollegeSearch/SortIcon';
import Dropdown from 'src/components/dropdown';
import TextButton from 'src/components/TextButton';
import Button from 'src/components/button';
import {UserType} from 'src/types/enums';
import 'stylesheets/components/college-search/college-search-header.scss';
import Icon from 'shared/components/icon';
import styles from './styles.module.scss';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import usePlatformContext from 'src/hooks/usePlatform';
import {CollegeSearch} from 'src/types/store';
import useFeatureFlags from 'src/hooks/useFeatureFlags';
import browserHistory from 'shared/history';

interface SortOption {
  value: string;
  type: string;
  order: string;
}

interface HeaderProps {
  collegeSearch: CollegeSearch;
  clearFilters: () => void;
  updateSortBy: (type: string) => void;
  resetPage: () => void;
  getColleges: () => void;
  openModal: (modalName: string) => void;
  updateSortOrder: (order: string) => void;
  userType: string;
}

const sortOptions = (options: SortOption[]) =>
  options.map((sort) => {
    const {value, type, order} = sort;

    return {value: `${type}|||${order}`, text: value, _id: `${type}|||${order}`};
  });

function Header({
  collegeSearch,
  clearFilters,
  updateSortBy,
  resetPage,
  getColleges,
  openModal,
  updateSortOrder,
  userType,
}: HeaderProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const {isIonic} = usePlatformContext();
  const mq = useMediaQuery();
  const {collegeSearchShare} = useFeatureFlags(['collegeSearchShare']);

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.addEventListener('touchmove', (e) => {
        e.preventDefault();
      });
    }
  }, []);

  const reloadColleges = () => {
    resetPage();
    getColleges();
  };

  const {totalColleges, sortBy, sortOrder} = collegeSearch;

  let options;
  switch (userType) {
    case UserType.HS_STUDENT:
      options = sortOptions(HS_SORT_OPTIONS);
      break;
    case UserType.CC_STUDENT:
      options = sortOptions(TRANSFER_SORT_OPTIONS);
      break;
    case UserType.EDUCATOR:
      options = sortOptions(EDUCATOR_SORT_OPTIONS);
      break;
    case UserType.PUBLIC:
      options = sortOptions(PUBLIC_SORT_OPTIONS);
      break;
    default:
      throw new Error('This user type should not be here');
  }

  const sort = find(options, {value: `${sortBy}|||${sortOrder}`});

  return (
    <div
      className={classnames('college-search-header', {
        educator: userType === UserType.EDUCATOR,
        '--ionic': isIonic,
      })}
      ref={headerRef}
    >
      <span className="college-search-header-filters">
        <Text weight={3} inline>
          Filter
        </Text>
        <TextButton
          buttonName="college-search-clear-filters"
          color="red"
          onClick={clearFilters}
          aria-label="Clear All Filters"
        >
          Clear All
        </TextButton>
      </span>
      <h1
        className={classnames('college-search-header-title', {'--ionic': isIonic})}
        aria-live="polite"
        data-testid="college-search-total-count"
      >
        <span className="shared-font bold">{totalColleges}</span>{' '}
        {pluralize(totalColleges, 'College')}
      </h1>
      <span className="college-search-header-sort">
        <span className="college-search-header-sort-by">
          {mq.SM_AND_UP && (
            <Text weight={5} inline emphasis={mq.MS_AND_DOWN}>
              Sort By
            </Text>
          )}
        </span>
        <span className={classnames('college-search-header-sort-dropdown', {mobile: isIonic})}>
          {mq.MS_AND_DOWN ? (
            <div className="college-search-header-mobile-sort-container">
              <div
                onClick={() => {
                  openModal('college-search-sort-by');
                }}
                id="open-sort-by-ionic-picker"
              >
                <span
                  className="college-search-header-mobile-sort"
                  aria-label={`Results sorted by ${sort?.text} click here to change sorting order`}
                >
                  {sort?.text}
                </span>
                <SortIcon className="college-search-header-mobile-sort-icon" size={20} />
              </div>
              {collegeSearchShare && (
                <div
                  onClick={() => {
                    browserHistory.replace({
                      pathname: `${location.pathname}/share`,
                      state: {shareUrl: window.location.href},
                    });
                  }}
                  style={{marginLeft: '12px'}}
                >
                  <Icon
                    iconType="external-link-alt"
                    className="college-search-header-mobile-share-icon"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className={styles['sort-dropdown-container']}>
              <Dropdown
                onChange={(e) => {
                  const [type, order] = e.target.value.split('|||');
                  updateSortBy(type);
                  updateSortOrder(order);
                  reloadColleges();
                }}
                options={options}
                value={`${sortBy}|||${sortOrder}`}
              />
              {collegeSearchShare && (
                <span className={styles.shareButtonContainer}>
                  <Button
                    size="medium"
                    onClick={() => {
                      browserHistory.replace({
                        pathname: `${location.pathname}/share`,
                        state: {shareUrl: window.location.href},
                      });
                    }}
                  >
                    <Icon iconType="external-link-alt" />
                  </Button>
                </span>
              )}
            </div>
          )}
        </span>
      </span>
    </div>
  );
}

export default Header;
